import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Trash2, RefreshCw, Copy, Check } from 'lucide-react';
import { Breadcrumbs } from '@/components/Breadcrumbs';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { apiClient } from '@/lib/apiClient';

interface EmailDomain {
  id: string;
  domain: string;
  dkimSelector: string;
  spfVerified: boolean;
  dkimVerified: boolean;
  dmarcVerified: boolean;
  lastVerifiedAt: string | null;
  createdAt: string;
}

interface DnsRecord {
  type: string;
  host: string;
  value: string;
  purpose: string;
}

interface DnsSetupResult {
  domainId: string;
  domain: string;
  dnsRecords: DnsRecord[];
}

interface SuppressionEntry {
  recipientType: string;
  recipientId: string;
  email: string;
  emailStatus: string;
  suppressedAt: string;
}

export function EmailDomainSetupPage() {
  const { t } = useTranslation();
  const [domains, setDomains] = useState<EmailDomain[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [setupResult, setSetupResult] = useState<DnsSetupResult | null>(null);
  const [domainInput, setDomainInput] = useState('');
  const [selectorInput, setSelectorInput] = useState('');
  const [registering, setRegistering] = useState(false);
  const [verifying, setVerifying] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [suppression, setSuppression] = useState<SuppressionEntry[]>([]);
  const [showSuppression, setShowSuppression] = useState(false);
  const [copiedKey, setCopiedKey] = useState<string | null>(null);

  const load = async () => {
    try {
      const res = await apiClient.get<EmailDomain[]>('/api/v1/admin/email-domains');
      setDomains(res.data);
    } finally {
      setLoading(false);
    }
  };

  const loadSuppression = async () => {
    const res = await apiClient.get<SuppressionEntry[]>('/api/v1/admin/email-domains/suppression-list');
    setSuppression(res.data);
    setShowSuppression(true);
  };

  useEffect(() => { load(); }, []);

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(null), 3500);
  };

  const handleRegister = async () => {
    if (!domainInput.trim()) return;
    setRegistering(true);
    try {
      const res = await apiClient.post<DnsSetupResult>('/api/v1/admin/email-domains', {
        domain: domainInput.trim().toLowerCase(),
        dkimSelector: selectorInput.trim() || null,
      });
      setSetupResult(res.data);
      setShowModal(false);
      await load();
    } finally {
      setRegistering(false);
    }
  };

  const handleVerify = async (domainId: string) => {
    setVerifying(domainId);
    try {
      await apiClient.post(`/api/v1/admin/email-domains/${domainId}/verify`);
      await load();
      showToast(t('emailDomainSetup.verification_done'));
    } finally {
      setVerifying(null);
    }
  };

  const handleViewDns = async (domainId: string) => {
    const res = await apiClient.get<DnsSetupResult>(`/api/v1/admin/email-domains/${domainId}/dns-records`);
    setSetupResult(res.data);
  };

  const handleDelete = async (domainId: string) => {
    if (!confirm(t('emailDomainSetup.delete_confirm'))) return;
    await apiClient.delete(`/api/v1/admin/email-domains/${domainId}`);
    await load();
    showToast(t('emailDomainSetup.deleted'));
  };

  const copyToClipboard = (text: string, key: string) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedKey(key);
      setTimeout(() => setCopiedKey(null), 2000);
    });
  };

  const VerifiedBadge = ({ ok }: { ok: boolean }) => (
    <span className={`px-2 py-0.5 rounded text-xs font-medium ${
      ok ? 'bg-green-900/40 text-green-200' : 'bg-red-900/40 text-red-200'
    }`}>
      {ok ? t('emailDomainSetup.verified') : t('emailDomainSetup.not_verified')}
    </span>
  );

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-6">
      <Breadcrumbs crumbs={[{ label: t('emailDomainSetup.title') }]} />
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">{t('emailDomainSetup.title')}</h1>
          <p className="text-muted-foreground text-sm mt-1">{t('emailDomainSetup.subtitle')}</p>
        </div>
        <div className="flex gap-2">
          <Button variant="ghost" onClick={loadSuppression}>
            {t('emailDomainSetup.suppression_list')}
          </Button>
          <Button onClick={() => setShowModal(true)}>
            <Plus className="w-4 h-4 mr-2" />
            {t('emailDomainSetup.add_domain')}
          </Button>
        </div>
      </div>

      {toast && (
        <div className="bg-green-900/40 text-green-200 px-4 py-2 rounded text-sm">{toast}</div>
      )}

      {loading ? (
        <p className="text-muted-foreground">{t('common.loading')}</p>
      ) : domains.length === 0 ? (
        <p className="text-muted-foreground text-sm">{t('emailDomainSetup.empty')}</p>
      ) : (
        <div className="border rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted/40">
              <tr>
                <th className="text-left p-3 font-medium">{t('emailDomainSetup.table.domain')}</th>
                <th className="text-left p-3 font-medium">{t('emailDomainSetup.table.spf')}</th>
                <th className="text-left p-3 font-medium">{t('emailDomainSetup.table.dkim')}</th>
                <th className="text-left p-3 font-medium">{t('emailDomainSetup.table.dmarc')}</th>
                <th className="text-left p-3 font-medium">{t('emailDomainSetup.table.verified_at')}</th>
                <th className="text-left p-3 font-medium">{t('emailDomainSetup.table.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {domains.map((domain) => (
                <tr key={domain.id} className="border-t hover:bg-muted/20">
                  <td className="p-3 font-mono text-xs">{domain.domain}</td>
                  <td className="p-3"><VerifiedBadge ok={domain.spfVerified} /></td>
                  <td className="p-3"><VerifiedBadge ok={domain.dkimVerified} /></td>
                  <td className="p-3"><VerifiedBadge ok={domain.dmarcVerified} /></td>
                  <td className="p-3 text-xs text-muted-foreground">
                    {domain.lastVerifiedAt ? new Date(domain.lastVerifiedAt).toLocaleString() : '—'}
                  </td>
                  <td className="p-3 flex gap-1 flex-wrap">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleViewDns(domain.id)}
                    >
                      DNS
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleVerify(domain.id)}
                      disabled={verifying === domain.id}
                    >
                      <RefreshCw className="w-3.5 h-3.5 mr-1" />
                      {t('emailDomainSetup.verify')}
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => handleDelete(domain.id)}>
                      <Trash2 className="w-3.5 h-3.5 text-destructive" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* DNS records panel */}
      {setupResult && (
        <div className="border rounded-lg p-4 space-y-3">
          <h2 className="text-base font-semibold">{t('emailDomainSetup.dns_records')}</h2>
          <p className="text-xs text-muted-foreground font-mono">{setupResult.domain}</p>
          <div className="space-y-3">
            {setupResult.dnsRecords.map((rec, i) => (
              <div key={i} className="bg-muted/30 rounded p-3 space-y-1">
                <div className="flex justify-between items-center">
                  <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                    {rec.purpose}
                  </span>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => copyToClipboard(rec.value, `${i}`)}
                  >
                    {copiedKey === `${i}` ? (
                      <Check className="w-3.5 h-3.5 text-green-600" />
                    ) : (
                      <Copy className="w-3.5 h-3.5" />
                    )}
                  </Button>
                </div>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <span className="text-muted-foreground">{t('emailDomainSetup.dns_record.type')}:</span>
                  <span className="font-mono">{rec.type}</span>
                  <span className="text-muted-foreground">{t('emailDomainSetup.dns_record.host')}:</span>
                  <span className="font-mono break-all">{rec.host}</span>
                  <span className="text-muted-foreground">{t('emailDomainSetup.dns_record.value')}:</span>
                  <span className="font-mono break-all text-[10px]">{rec.value}</span>
                </div>
              </div>
            ))}
          </div>
          <Button variant="ghost" size="sm" onClick={() => setSetupResult(null)}>
            {t('common.cancel')}
          </Button>
        </div>
      )}

      {/* Suppression list panel */}
      {showSuppression && (
        <div className="border rounded-lg p-4 space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="text-base font-semibold">{t('emailDomainSetup.suppression_list')}</h2>
            <Button size="sm" variant="ghost" onClick={() => setShowSuppression(false)}>
              {t('common.cancel')}
            </Button>
          </div>
          {suppression.length === 0 ? (
            <p className="text-sm text-muted-foreground">{t('emailDomainSetup.suppression_empty')}</p>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-muted/40">
                <tr>
                  <th className="text-left p-2 font-medium">{t('emailDomainSetup.suppression.email')}</th>
                  <th className="text-left p-2 font-medium">{t('emailDomainSetup.suppression.type')}</th>
                  <th className="text-left p-2 font-medium">{t('emailDomainSetup.suppression.status')}</th>
                  <th className="text-left p-2 font-medium">{t('emailDomainSetup.suppression.suppressed_at')}</th>
                </tr>
              </thead>
              <tbody>
                {suppression.map((entry) => (
                  <tr key={`${entry.recipientId}`} className="border-t">
                    <td className="p-2 font-mono text-xs">{entry.email}</td>
                    <td className="p-2 text-xs">{entry.recipientType}</td>
                    <td className="p-2">
                      <span className="px-2 py-0.5 rounded text-xs bg-red-900/40 text-red-200">
                        {t(`emailDomainSetup.status.${entry.emailStatus}`) || entry.emailStatus}
                      </span>
                    </td>
                    <td className="p-2 text-xs text-muted-foreground">
                      {new Date(entry.suppressedAt).toLocaleString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Register domain modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-background rounded-xl shadow-xl p-6 w-full max-w-md space-y-4">
            <h2 className="text-lg font-semibold">{t('emailDomainSetup.modal.register_title')}</h2>
            <div className="space-y-3">
              <label className="block text-sm font-medium">{t('emailDomainSetup.modal.domain')}</label>
              <Input
                value={domainInput}
                onChange={(e) => setDomainInput(e.target.value)}
                placeholder="sales.yourcompany.com"
              />
              <label className="block text-sm font-medium">{t('emailDomainSetup.modal.selector')}</label>
              <Input
                value={selectorInput}
                onChange={(e) => setSelectorInput(e.target.value)}
                placeholder="kairos"
              />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="ghost" onClick={() => setShowModal(false)}>{t('common.cancel')}</Button>
              <Button onClick={handleRegister} disabled={registering || !domainInput.trim()}>
                {t('common.create')}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
